// =========================================================
// * Vue Material Dashboard - v1.2.1
// =========================================================
//
// * Product Page: https://www.creative-tim.com/product/vue-material-dashboard
// * Copyright 2019 Creative Tim (https://www.creative-tim.com)
// * Licensed under MIT (https://github.com/creativetimofficial/vue-material-dashboard/blob/master/LICENSE.md)
//
// * Coded by Creative Tim
//
// =========================================================
//
// * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import Vue from "vue";
import Router from "vue-router";
import App from "./App";
import jwt_decode from "jwt-decode";
import Vuex from "vuex";
// router setup
import routes from "./routes/routes";

// sentry
import * as Sentry from "@sentry/browser";
import * as Integrations from "@sentry/integrations";

import VueApexCharts from "vue-apexcharts";

// Plugins
import GlobalComponents from "./globalComponents";
import GlobalDirectives from "./globalDirectives";
import Notifications from "./components/NotificationPlugin";
import VueCompositionAPI from "@vue/composition-api";
// MaterialDashboard plugin
import MaterialDashboard from "./material-dashboard";

import Chartist from "chartist";

// VueECharts***
// *************
import ECharts from "vue-echarts"; // refers to components/ECharts.vue in webpack
// import ECharts modules manually to reduce bundle size
import "echarts/lib/chart/bar";
import "echarts/lib/component/tooltip";
import "echarts/lib/component/legend";

// register component to use
Vue.component("v-chart", ECharts);

// Import global config
import SAMAN_CONFIG from "./global-config.js";

import apiManager from "./api-manager";
import { forceLogout, refreshAccessToken } from "./utils/authStorageManager";
import store from "./store";
import moment from "moment";

// Vue Highlight JS
import VueHighlightJS from "vue-highlightjs";
import "highlight.js/styles/default.css";



// configure router
const router = new Router({
  routes, // short for routes: routes
  linkActiveClass: "nav-item active",
  scrollBehavior(to, from, savedPosition) {
    return { x: 0, y: 0 };
  }
});

router.beforeEach((to, from, next) => {
  const { breadCrumbs, authentication } = store.state;

  if (from.path !== "/" && to.path.startsWith(from.path)) {
    const fromCrumbs = breadCrumbs[from.name] || [];
    const newPath = { name: from.name, path: from.path };

    if (!breadCrumbs[to.name]) {
      breadCrumbs[to.name] = [...fromCrumbs, newPath];
    }
  }

  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  const hasToken = !!authentication.accessToken;

  if (requiresAuth && !hasToken) {
    return next("/login");
  }

  let adminGroups = [];
  try {
    const raw = window.localStorage.getItem("adminGroups");
    if (raw) adminGroups = JSON.parse(raw);
  } catch (_) {
    adminGroups = [];
  }

  const hasPermission = to.matched.every(record => {
    if (!record.meta.permissionGroups) return true;
    return adminGroups.some(group => record.meta.permissionGroups.includes(group));
  });

  if (to.path === "/dashboard-loader") return next();

  if (hasToken && adminGroups.length > 0) {
    if (!hasPermission) return next("/dashboard-loader");
    return next();
  }

  return next();
});

Vue.prototype.$Chartist = Chartist;

Vue.config.errorHandler = function(err, vm, info) {
  console.log(`Error: ${err.toString()}\nInfo: ${info}`);
};

Vue.config.warnHandler = function(msg, vm, trace) {
  console.log(`Warn: ${msg}\nTrace: ${trace}`);
};
Vue.use(Router);
Vue.use(MaterialDashboard);
Vue.use(GlobalComponents);
Vue.use(GlobalDirectives);
Vue.use(Notifications);

Vue.use(VueCompositionAPI);
Vue.use(VueApexCharts);
Vue.component("apexchart", VueApexCharts);
Vue.use(VueHighlightJS);

import VueDOMPurifyHTML from "vue-dompurify-html";
import bufferToBase64 from "./utils/bufferToBase64";
Vue.use(VueDOMPurifyHTML);
/* eslint-disable no-new */

// sentry config
// Sentry.init({
//   dsn: "https://c421376a1b314d65bbd62a20a1a94915@sentry.smdev.ir/5",
//   integrations: [new Integrations.Vue({Vue, attachProps: true})]
// });
Vue.prototype.$api = apiManager;

(async () => {
  try {
    await refreshAccessToken();
  } catch (err) {
    forceLogout();
  }

  new Vue({
    el: "#app",
    render: h => h(App),
    router,
    store,
    data: {
      Chartist
    }
  });
})();

Vue.filter("capitalize", function (value) {
  return value.charAt(0).toUpperCase() + value.slice(1);
});

Vue.filter("formatLoginAttemptTime", function (value) {
  if (!value) return "";
  return moment(value).format("MMMM Do YYYY, h:mm:ss a"); // April 3rd 2021, 5:23:32 am
});

if (!String.prototype.format) {
  String.prototype.format = function () {
    const args = arguments;
    return this.replace(/{(\d+)}/g, function (match, number) {
      return typeof args[number] != "undefined" ? args[number] : match;
    });
  };
}

Credential.prototype.toFidoResponse = function () {
  const tokenResponse = {
    id: this.id,
    rawId: bufferToBase64(this.rawId),
    response: {
      attestationObject: bufferToBase64(this.response.attestationObject),
      clientDataJSON: bufferToBase64(this.response.clientDataJSON),
    },
    type: this.type,
  };
  return JSON.stringify(tokenResponse);
};
